import React, { useState, useEffect, useCallback } from 'react';
import './TenantsPage.scss';
import UIButton from '@/components/shared/UIButton';
import UIPageTitle from '@/components/shared/UIPageTitle';
import TenantsSearchSection from '@/components/dashboard/Tenants/TenantsSearchSection';
import TenantsStatsSection from '@/components/dashboard/Tenants/TenantsStatsSection';
import ListOfDebtorsSection from '@/components/dashboard/Tenants/ListOfDebtorsSection';
import TenantsListSection, {
  TenantListData,
} from '@/components/dashboard/Tenants/TenantsListSection';
import { getAllTenantsRequest, searchTenantsRequest } from '@/api';
import { useNavigate } from 'react-router-dom';

const BackToDashboardButton = () => {
  const navigate = useNavigate();
  return (
    <UIButton
      outline={true}
      variant="primary"
      size="medium"
      onClick={() => navigate('/dashboard')}
    >
      Back to Dashboard
    </UIButton>
  );
};

const TenantsPage = () => {
  const [tenants, setTenants] = useState<TenantListData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [currentStatus, setCurrentStatus] = useState('all');
  const navigate = useNavigate();

  const adaptTenantData = (data: any[]): TenantListData[] => {
    return data.map((tenant: any) => {
      let tenantName = '';
      if (tenant.firstName && tenant.lastName) {
        tenantName = `${tenant.firstName} ${tenant.lastName}`;
      } else if (tenant.firstname && tenant.lastname) {
        tenantName = `${tenant.firstname} ${tenant.lastname}`;
      } else if (tenant.name) {
        tenantName = tenant.name;
      } else {
        tenantName = 'Unknown User';
      }

      return {
        id: tenant.id?.toString() || '',
        name: tenantName.trim(),
        email: tenant.email || '',
        avatar: tenant.avatar || undefined,
        paymentStatus: tenant.paymentStatus || tenant.status || 'not-set',
        property: tenant.property?.name || tenant.propertyName || 'N/A',
        unit: tenant.unit?.number || tenant.unitNumber || 'N/A',
      };
    });
  };

  const fetchTenants = useCallback(async (searchTerm: string, status: string) => {
    setIsLoading(true);
    try {
      let response;
      if (searchTerm.trim()) {
        response = await searchTenantsRequest(searchTerm, status);
      } else {
        response = await getAllTenantsRequest(status);
      }
      const adaptedData = adaptTenantData(response.data);
      setTenants(adaptedData);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setTenants([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants(currentSearchTerm, currentStatus);
  }, [fetchTenants, currentSearchTerm, currentStatus]);

  const handleSearch = (searchTerm: string, status: string) => {
    setCurrentSearchTerm(searchTerm);
    setCurrentStatus(status);
  };

  const handleStatusChange = (status: string) => {
    setCurrentStatus(status);
  };

  const handleTenantAction = (tenantId: string, action: string) => {
    console.log('Tenant action:', action, 'for tenant:', tenantId);
    if (action === 'chat') {
      navigate(`/dashboard/chats?chatId=${tenantId}`);
    }
  };

  const handleAddTenant = () => {
    navigate('/dashboard/tenants/add');
  };

  return (
    <>
      <UIPageTitle title="Tenants" action={<BackToDashboardButton />} />

      <TenantsSearchSection
        onSearch={handleSearch}
        onStatusChange={handleStatusChange}
        defaultStatus={currentStatus}
        defaultSearchTerm={currentSearchTerm}
        loading={isLoading}
      />

      <TenantsStatsSection />

      <TenantsListSection
        title="List of tenants"
        data={tenants}
        onTenantAction={handleTenantAction}
        onAddTenant={handleAddTenant}
        loading={isLoading}
        searchTerm={currentSearchTerm}
        statusFilter={currentStatus}
      />

      <ListOfDebtorsSection
        id="debtors-section"
        onTenantAction={(tenantId, action) =>
          console.log('Debtor action:', tenantId, action)
        }
      />
    </>
  );
};

export default TenantsPage;
